"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type AiImageLabels,
  aiImageIsReady,
  AI_IMAGE_FIELDS as F,
  formatAiImage,
} from "@/lib/tool-kit/media-sources/ai-image-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const AI_IMAGE_CITATION_GENERATOR_STRINGS = {
  formHeading: "Image details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the prompt you used and the AI tool to see your figure note, caption and citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  promptLabel: "Prompt",
  promptPlaceholder:
    "Create an expressionist-style image of two people standing on a beach looking at the ocean",
  promptHint:
    "Copy it exactly. If you refined the image over several prompts, give the main one here and describe the rest in your text.",
  toolLabel: "AI tool",
  toolPlaceholder: "DALL·E",
  toolHint:
    "As the company writes it: DALL·E, Midjourney, Adobe Firefly, Imagen.",
  modelLabel: "Model or version (if shown)",
  modelPlaceholder: "3",
  modelHint:
    "A number such as 3 or V7 becomes MLA's version 3; a name such as GPT Image 1 becomes model GPT Image 1.",
  companyLabel: "Company",
  companyPlaceholder: "OpenAI",
  dateLabel: "Date you generated the image",
  datePlaceholder: "YYYY-MM-DD",
  shareUrlLabel: "Share link to the image or chat (if any)",
  shareUrlPlaceholder: "https://chatgpt.com/share/…",
  shareUrlHint:
    "MLA and Chicago prefer a stable share link; MLA falls back to the tool's address.",
  toolUrlLabel: "Tool's web address (APA reference)",
  toolUrlPlaceholder: "https://chatgpt.com",
  toolYearLabel: "Year the tool was last updated (APA)",
  toolYearPlaceholder: "2024",
  toolYearHint:
    "APA dates the tool, not the image. If the tool doesn't say, use the copyright year on its site; left blank, the year you generated the image is used.",
  descriptionLabel: "Description in brackets (APA)",
  descriptionPlaceholder: "Text-to-image model",
  descriptionHint: "APA lets you choose the description that fits the tool.",
  figureNumberLabel: "Figure number in your paper",
  figureNumberPlaceholder: "1",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  figureNote: "Figure note (below the image)",
  toolReference: "Reference list (the AI tool)",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  caption: "Figure caption",
  worksCited: "Works Cited (if you also list it)",
  inText: "In-text citation",
  creditLine: "Credit line (caption)",
  footnote: "Footnote",
  apaNote:
    "APA (September 2025 guidance) says to indicate AI use in the figure note and to cite the AI tool generally, with a reference to the tool. The prompt goes in the note or your text, never in the reference.",
  mlaNote:
    "MLA (August 2025 guidance) describes the prompt, then gives the tool in italics, the model or version, the company, the date and a share link. The caption can carry the full citation, or a Works Cited entry can.",
  chicagoNote:
    "Chicago credits an AI image like any image, with the prompt in quotation marks. CMOS 18 (§14.112) keeps AI-generated content out of the bibliography: use the caption or a note.",
  example: {
    prompt:
      "Create an expressionist-style image of two people standing on a beach looking at the ocean",
    tool: "DALL·E",
    model: "3",
    company: "OpenAI",
    date: "2024-09-23",
    shareUrl: "https://chatgpt.com/share/66f1c3a3-3f90-8000-9750-82c57c4a6592",
    toolUrl: "https://chatgpt.com",
    toolYear: "2024",
    figureNumber: "1",
  },
} as const;

type Strings = typeof AI_IMAGE_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.prompt,
      kind: "textarea",
      label: strings.promptLabel,
      placeholder: strings.promptPlaceholder,
      hint: strings.promptHint,
    },
    {
      id: F.tool,
      kind: "text",
      label: strings.toolLabel,
      placeholder: strings.toolPlaceholder,
      hint: strings.toolHint,
    },
    {
      id: F.model,
      kind: "text",
      label: strings.modelLabel,
      placeholder: strings.modelPlaceholder,
      hint: strings.modelHint,
    },
    {
      id: F.company,
      kind: "text",
      label: strings.companyLabel,
      placeholder: strings.companyPlaceholder,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.shareUrl,
      kind: "url",
      label: strings.shareUrlLabel,
      placeholder: strings.shareUrlPlaceholder,
      hint: strings.shareUrlHint,
      wide: true,
    },
    {
      id: F.toolUrl,
      kind: "url",
      label: strings.toolUrlLabel,
      placeholder: strings.toolUrlPlaceholder,
    },
    {
      id: F.toolYear,
      kind: "text",
      label: strings.toolYearLabel,
      placeholder: strings.toolYearPlaceholder,
      hint: strings.toolYearHint,
    },
    {
      id: F.description,
      kind: "text",
      label: strings.descriptionLabel,
      placeholder: strings.descriptionPlaceholder,
      hint: strings.descriptionHint,
    },
    {
      id: F.figureNumber,
      kind: "text",
      label: strings.figureNumberLabel,
      placeholder: strings.figureNumberPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function aiImageLabels(strings: Strings): AiImageLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    figureNote: strings.figureNote,
    toolReference: strings.toolReference,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    caption: strings.caption,
    worksCited: strings.worksCited,
    inText: strings.inText,
    creditLine: strings.creditLine,
    footnote: strings.footnote,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function AiImageCitationGeneratorWidget() {
  const strings = useToolUi(AI_IMAGE_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => aiImageLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatAiImage(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={aiImageIsReady}
      strings={strings}
    />
  );
}
